import { type CollectionJSONObject, type CollectionSummaryJSONObject, IkeToken, IkeUserRole } from '@ikegps/core-api';
import { action, observable, makeObservable } from 'mobx';
import { Spinner } from '../components/spinner/spinner';
import { T, Translate } from '../i18n';
import type { MithrilVNode } from '../mithril';
import { CollectionHistoryTable } from '../modules/collection.history.table/collection.history.table';
import { Services } from '../services';
import { AppStore } from '../store/index';
import { IkePage, type IkePageConfig, IkePageName } from './page';

export const CollectionHistoryPageConfig: IkePageConfig = {
	type: 'internal',
	name: IkePageName.collectionHistory,
	path: '/collection/:collectionUuid/history',
	auth: {
		required: true,

		tokens: [IkeToken.FEATURE_REVISION_HISTORY],

		userLevel: [IkeUserRole.DATA_ADMIN, IkeUserRole.ROOT],
	},
};

export class CollectionHistoryPage extends IkePage {
	@observable private collectionUuid: string | null = null;
	@observable private collectionSummary: CollectionSummaryJSONObject | null = null;
	@observable private revisions: CollectionJSONObject[] = [];
	private modificationMessages = observable.map<string, string>();
	private userNames = observable.map<string, string>();

	constructor() {
		super(CollectionHistoryPageConfig);

		makeObservable(this);

		// Wait for the route params to be available
		this.reaction(
			() => Services.Route.params,
			(params) => {
				this.collectionUuid = params.get('collectionUuid') ?? null;

				if (!this.collectionUuid) {
					return;
				}

				// Get the CollectionSummary

				void Services.Department.rest.CollectionSummary.getByUuid(this.collectionUuid)
					.fetch()
					.then((c) => {
						this.setCollectionSummary(c);

						// Get the Collection revisions
						void AppStore.Collection.getHistory(c.uuid).then((history) => {
							history.sort((a, b) =>
								b.updatedAt && a.updatedAt ? new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime() : 0
							);

							this.setRevisions(history);

							// Load usernames
							history.forEach((r) => {
								if (r.updatedBy) {
									const updatedBy = r.updatedBy;
									void Services.Department.rest.Department.User.getProfileById(updatedBy)
										.fetch()
										.then((user) => {
											this.setUsername(updatedBy, user.username);
										});
								}

								// Load modification messages
								if (r.modifications && Object.keys(r.modifications).length) {
									// Load the moved from department message
									if (r.modifications?.departmentMovedFrom) {
										void Services.Department.rest.Department.getById({
											departmentId: r.modifications.departmentMovedFrom,
										}).then((result) => {
											if (result.results[0]) {
												this.modificationMessages.set(
													r.revision,
													Translate(T.COLLECTION_REVISION_HISTORY_MOVED_FROM_DEPARTMENT_MESSAGE, result.results[0].name)
												);
											}
										});
									}

									// Load the reverted to object message
									if (r.modifications?.revisionToRevertTo) {
										this.modificationMessages.set(
											r.revision,
											Translate(T.COLLECTION_REVISION_HISTORY_REVERTED_MESSAGE, r.modifications.revisionToRevertTo)
										);
									}

									if (r.modifications?.formMigrate) {
										this.modificationMessages.set(
											r.revision,
											Translate(T.COLLECTION_REVISION_HISTORY_FORM_MIGRATED_MESSAGE, {
												from: r.modifications.formMigrate.from,
												to: r.modifications.formMigrate.to,
											})
										);
									}

									if (r.modifications?.formUpdated) {
										const formUpdated = r.modifications.formUpdated;
										void AppStore.Form.fetch(formUpdated.fromRevision).then((form) =>
											this.modificationMessages.set(
												r.revision,
												Translate(T.COLLECTION_REVISION_HISTORY_FORM_UPGRADED_MESSAGE, {
													title: form.state.displayName,
													from: formUpdated.fromVersion,
													to: formUpdated.toVersion,
												})
											)
										);
									}

									if (r.modifications?.spanLinkedPoleUuid) {
										void AppStore.CollectionSummary.fetchByUuid(r.modifications.spanLinkedPoleUuid).then(
											(collectionSummary) => {
												this.modificationMessages.set(
													r.revision,
													Translate(T.COLLECTION_REVISION_HISTORY_SPAN_LINK_MESSAGE, {
														poleTitle: collectionSummary.title,
													})
												);
											}
										);
									}
								} else {
									// Set the modifications to an empty string if none need to be loaded
									this.modificationMessages.set(r.revision, '');
								}
							});
						});
					});
			},
			{ fireImmediately: true }
		);
	}

	@action setCollectionSummary(c: CollectionSummaryJSONObject) {
		this.collectionSummary = c;
	}

	@action setRevisions(rs: CollectionJSONObject[]) {
		this.revisions = rs;
	}

	@action setUsername(userId: string, username: string) {
		this.userNames.set(userId, username);
	}

	@action setModificationMessages(revision: string, message: string) {
		this.modificationMessages.set(revision, message);
	}

	get nodes(): MithrilVNode {
		if (Services.Department.current == null) {
			return null;
		}

		if (!this.collectionSummary || !this.revisions.length) {
			return Spinner.view();
		}

		return CollectionHistoryTable.view(
			this.collectionSummary,
			this.revisions,
			Object.fromEntries(this.userNames),
			Object.fromEntries(this.modificationMessages)
		);
	}
}
